import { ChangeDetectionStrategy, Component, inject, Input } from '@angular/core';
import { type BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { filter, take } from 'rxjs/operators';

import { type Domain } from '@common/types/domain.type';
import type { SiteEntity } from '@common/types/site.type';
import type { SitePageEntity } from '@common/types/site-page.type';

import { PagesService } from '@web/store/website/pages/pages.service';

import { ModalsFacade } from '@web/store/modals/modals.facade';
import { PagesFacade } from '@web/store/website/pages/pages.facade';
import { PageSettingsFacade } from '@web/store/website/page-settings/page-settings.facade';

import { DeletePageModalComponent } from '@web/layout/drawers/page-drawer/modals/delete-page-modal/delete-page-modal.component';
import { EditPageSettingsModalComponent } from '@web/layout/drawers/page-drawer/modals/edit-page-settings-modal/edit-page-settings-modal.component';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { NgIf } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { TooltipModule } from 'ngx-bootstrap/tooltip';

@Component({
    selector: 'web-pages-drawer-item',
    templateUrl: './pages-drawer-item.component.html',
    styleUrls: ['./pages-drawer-item.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [BsDropdownModule, NgIf, TranslateModule, TooltipModule],
})
export class PagesDrawerItemComponent {
    @Input() public site: SiteEntity;
    @Input() public page: SitePageEntity;
    @Input() public pages: SitePageEntity[];
    @Input() public pageRouterId: number;
    @Input() public siteId: number;
    @Input() public domains: Domain[];
    @Input() public isSaving: boolean;

    private readonly pageSettingsFacade: PageSettingsFacade = inject(PageSettingsFacade);
    private readonly pagesFacade: PagesFacade = inject(PagesFacade);
    private readonly modalsFacade: ModalsFacade = inject(ModalsFacade);
    private readonly modalService: BsModalService = inject(BsModalService);
    private readonly pagesService: PagesService = inject(PagesService);

    public get name(): string {
        return this.page.name;
    }

    public get isActive(): boolean {
        return this.pageRouterId == this.page.id;
    }

    public navigatePage(): void {
        if (this.isActive || this.isSaving) {
            return;
        }

        this.pagesFacade.navigateSitePageFromDrawer(this.siteId.toString(), this.page.id.toString());
    }

    public showPageSettingsModal(page: SitePageEntity): void {
        this.pageSettingsFacade
            .pageSettings$(this.siteId, this.page.id)
            .pipe(
                filter((pageSettings) => !!pageSettings),
                take(1)
            )
            .subscribe((pageSettings) => {
                this.modalService.show(EditPageSettingsModalComponent, {
                    initialState: {
                        page,
                        pageSettings,
                        siteId: this.siteId,
                        siteDomain: this.site.url,
                        disableUrl:
                            this.isMainPage(page) ||
                            this.isWebEducationPage ||
                            this.isWebChatBotPage ||
                            this.isCartPage,
                    },
                });
            });
    }

    public activatePage(): void {
        if (!this.isNewPage) {
            this.pagesFacade.activatePage(this.siteId as number, this.page.id as number);
            return;
        }

        this.modalsFacade.showSaveConfirmModal(false, true);
    }

    public disablePage(): void {
        this.pagesFacade.disablePage(this.siteId as number, this.page.id as number);
    }

    public deletePage(): void {
        if (this.isNewPage) {
            this.pagesFacade.deletePageSuccess(this.page.id);
            if (this.isActive && this.pages[0]) {
                this.pagesFacade.navigateSitePageFromDrawer(this.siteId.toString(), this.pages[0].id.toString());
            }

            return;
        }

        const modal: BsModalRef<DeletePageModalComponent> = this.modalService.show(DeletePageModalComponent, {
            initialState: {
                page: this.page,
            },
        });

        modal.content?.onClose.subscribe((result: boolean) => {
            if (!result) {
                return;
            }

            this.pagesFacade.deletePage(this.siteId as number, this.page.id as number);
        });
    }

    public copyPage(): void {
        this.pagesFacade.copyPageHandler(this.siteId, this.page);
    }

    public get isPageActive(): boolean {
        return this.pagesService.isPageActive(this.page);
    }

    public isMainPage(page: SitePageEntity): boolean {
        if (!this.pages[0]) {
            return false;
        }

        return this.pages[0].id === page.id;
    }

    public get isWebEducationPage(): boolean {
        return this.pagesService.isWebEducationPage(this.page);
    }

    public get isWebChatBotPage(): boolean {
        return this.pagesService.isWebChatBotPage(this.page);
    }

    public get isNewPage(): boolean {
        return this.pagesService.isPageNotSaved(this.page);
    }

    public get isCartPage(): boolean {
        return this.page?.url?.split('/')?.at(-1) === 'cart';
    }
}
